import styled, { css } from "styled-components";
import { useRef, useEffect } from "react";
import { usePage } from "@inertiajs/react";

import {
  z,
  weight,
  type,
  tracking,
  shadow,
  radius,
  motion,
  font,
  color,
} from "../../../styles/tokens";

/* Language switcher: a pill showing the current language, opening a panel that names both in full.
 *
 * Replaces the EN|ID segmented pill in the header and the pair of links in the brand-page footer.
 * Two initials side by side never said which one was current — the active half only differed by
 * fill — and neither surface named the languages, so "ID" had to be guessed at.
 *
 * Built on <details>, so it opens and closes with no JavaScript at all and is keyboard-operable for
 * free. The effect below only adds the two behaviours <details> does not have: close on Escape and
 * close on an outside click.
 */

const LANGUAGES = [
  { code: "en", label: "English", tag: "EN" },
  { code: "id", label: "Bahasa Indonesia", tag: "ID" },
];

/* Flags as inline SVG rather than emoji: emoji flags render as two letters on Windows, which would
 * put "GB" and "ID" next to the codes they are meant to illustrate. */
const FLAGS = {
  en: (
    <svg viewBox="0 0 20 15" aria-hidden="true" focusable="false">
      <rect width="20" height="15" fill="#012169" />
      <path d="M0,0 20,15 M20,0 0,15" stroke="#fff" strokeWidth="3" />
      <path d="M0,0 20,15 M20,0 0,15" stroke="#c8102e" strokeWidth="1.4" />
      <path d="M10,0 V15 M0,7.5 H20" stroke="#fff" strokeWidth="4" />
      <path d="M10,0 V15 M0,7.5 H20" stroke="#c8102e" strokeWidth="2.4" />
    </svg>
  ),
  id: (
    <svg viewBox="0 0 20 15" aria-hidden="true" focusable="false">
      <rect width="20" height="7.5" fill="#e70011" />
      <rect y="7.5" width="20" height="7.5" fill="#fff" />
    </svg>
  ),
};

const Flag = styled.span`
  display: flex;
  flex-shrink: 0;
  width: 20px;
  height: 15px;
  border-radius: 3px;
  overflow: hidden;
  /* An inset ring rather than a border, so the white half of the Indonesian flag still reads as
   * part of a flag on a white menu row instead of shrinking it to a red bar. */
  box-shadow: inset 0 0 0 1px rgba(20, 21, 15, 0.35);

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const Root = styled.details`
  position: relative;
  display: inline-flex;

  /* The default disclosure triangle, in every engine that draws one. */
  summary {
    list-style: none;
  }
  summary::-webkit-details-marker,
  summary::marker {
    display: none;
    content: "";
  }
`;

const Trigger = styled.summary`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 11px;
  border-radius: ${radius.pill};
  font-family: ${font.body};
  font-size: ${type.sm};
  font-weight: ${weight.semibold};
  cursor: pointer;
  user-select: none;
  transition:
    background ${motion.base},
    border-color ${motion.base};

  ${(props) =>
    props.$tone === "dark"
      ? css`
          border: 1px solid rgba(246, 244, 238, 0.24);
          background: rgba(246, 244, 238, 0.08);
          color: ${color.onDark};

          &:hover {
            background: rgba(246, 244, 238, 0.16);
            border-color: rgba(246, 244, 238, 0.4);
          }
        `
      : css`
          border: 1px solid ${color.border};
          background: ${color.surface};
          color: ${color.ink};

          &:hover {
            border-color: ${color.borderStrong};
          }
        `}
`;

const Chevron = styled.svg`
  width: 14px;
  height: 14px;
  opacity: 0.7;
  transition: transform ${motion.fast};

  details[open] & {
    transform: rotate(180deg);
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  /* Anchored to whichever edge keeps the 210px panel inside its container: right in the header,
   * left in the mobile drawer, where a right-anchored panel overflowed the drawer entirely. */
  ${(props) => (props.$align === "left" ? "left: 0;" : "right: 0;")}
  z-index: ${z.overlay};
  min-width: 210px;
  padding: 6px;
  border: 1px solid ${color.border};
  border-radius: ${radius.lg};
  background: ${color.surface};
  box-shadow: ${shadow.lg};

  /* Opens upward where the switcher sits in a footer, so the panel is not clipped by the page end. */
  ${(props) =>
    props.$drop === "up"
      ? css`
          top: auto;
          bottom: calc(100% + 8px);
        `
      : ""}
`;

/* A PLAIN anchor, deliberately not an Inertia Link.
 *
 * An Inertia visit swaps the page component but leaves everything Rails rendered into the document
 * untouched — <html lang>, the <title>, the canonical, the hreflang alternates and the JSON-LD all
 * come from shared/_page_seo on a full request. Switching language client-side therefore left the
 * document declaring the wrong language to screen readers and the wrong locale to crawlers. The old
 * router.visit() switcher had the same defect. A full navigation is the correct behaviour here.
 */
const Item = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: ${radius.sm};
  color: ${color.ink};
  transition: background ${motion.fast};

  &:hover {
    background: ${color.canvasAlt};
  }

  ${(props) =>
    props.$active
      ? css`
          background: ${color.primarySoft};
        `
      : ""}
`;

const Name = styled.span`
  flex: 1;
  font-size: ${type.sm};
  white-space: nowrap;
`;

const Tag = styled.span`
  font-size: 10px;
  letter-spacing: ${tracking.eyebrow};
  color: ${(props) => (props.$active ? color.primaryText : color.ink500)};
`;

/* Built from the Inertia page url, NOT window.location: this renders during SSR too, and a real
 * href means the alternate language is a crawlable link and survives a middle-click. */
const localeHref = (url, code) => {
  const path = (url || "/").split(/[?#]/)[0];
  const bare = path.startsWith("/id/")
    ? path.slice(3)
    : path === "/id"
      ? "/"
      : path;
  if (code !== "id") return bare || "/";
  return bare === "/" ? "/id" : `/id${bare}`;
};

const LanguageSwitch = (props) => {
  const page = usePage();
  const current = page.props.locale === "id" ? "id" : "en";
  const active =
    LANGUAGES.find((language) => language.code === current) || LANGUAGES[0];
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const close = () => {
      root.open = false;
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape" && root.open) {
        close();
        root.querySelector("summary")?.focus();
      }
    };
    const onPointerDown = (event) => {
      if (root.open && !root.contains(event.target)) close();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, []);

  return (
    <Root ref={rootRef} className={props.className}>
      <Trigger
        $tone={props.tone || "light"}
        aria-label={`Language: ${active.label}`}
      >
        <Flag>{FLAGS[active.code]}</Flag>
        <span>{active.tag}</span>
        <Chevron
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </Chevron>
      </Trigger>

      <Menu role="menu" $drop={props.drop} $align={props.align}>
        {LANGUAGES.map((language) => (
          <Item
            key={language.code}
            role="menuitem"
            href={localeHref(page.url, language.code)}
            hrefLang={language.code}
            lang={language.code}
            $active={language.code === current}
            aria-current={language.code === current ? "true" : undefined}
          >
            <Flag>{FLAGS[language.code]}</Flag>
            <Name>{language.label}</Name>
            <Tag $active={language.code === current}>{language.tag}</Tag>
          </Item>
        ))}
      </Menu>
    </Root>
  );
};

export default LanguageSwitch;
